import React, { useEffect, useMemo, useState } from "react";
import { ref, get, remove } from "firebase/database";
import { doc, getDoc } from "firebase/firestore";
import { auth, database, firestore } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import RecordPurchaseModal from "./RecordPurchaseModal";
import { Calendar, Edit2, Trash2 } from "lucide-react";
import { logRecordAuditEvent } from "../../../utils/recordAuditLog";
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DEFAULT_MILK_SOLD_PAGE_SIZE,
  MILK_SOLD_PAGE_SIZE_OPTIONS,
  buildMilkSoldChartData,
  getMilkSoldTotalPages,
  milkSoldRangeForPage,
  paginateMilkSoldItems,
} from "./milkSoldInsights";

const PIE_COLORS = ["#0891b2", "#06b6d4", "#14b8a6", "#22c55e", "#6366f1", "#a855f7"];

const MilkSoldTab = ({ companyName }) => {
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [activeClient, setActiveClient] = useState(null);
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsPageSize, setRecordsPageSize] = useState(DEFAULT_MILK_SOLD_PAGE_SIZE);

  const canManageSales = userRole === "CompanyAdmin" || userRole === "Manager";

  const parseDateParts = (dateStr) => {
    const dObj = new Date(dateStr);
    const y = dObj.getFullYear();
    const m = dObj.toLocaleString("en-US", { month: "long" });
    const d = dObj.getDate();
    return { y: String(y), m, d: String(d) };
  };

  const loadUserRole = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const userDoc = await getDoc(doc(firestore, "users", user.uid));
      let role = null;
      let companyFromUser = companyName;
      if (userDoc.exists()) {
        const data = userDoc.data();
        role = data?.role || data?.accountType || null;
        companyFromUser = data?.companyName || companyFromUser;
        if (companyFromUser) {
          const employeeDoc = await getDoc(
            doc(firestore, "companies", companyFromUser, "employees", user.uid)
          );
          if (employeeDoc.exists()) {
            role = employeeDoc.data()?.role || role;
          }
        }
      }

      if (!role) {
        const realtimeSnap = await get(ref(database, `Users/${user.uid}`));
        if (realtimeSnap.exists()) {
          const val = realtimeSnap.val() || {};
          role = val.role || val.accountType || role;
        }
      }
      setUserRole(role);
    } catch (err) {
      console.error("Failed to resolve user role", err);
    }
  };

  const loadSalesForDate = async (dateStr) => {
    if (!companyName || !dateStr) {
      setSales([]);
      return;
    }
    setLoading(true);
    try {
      const { y, m, d } = parseDateParts(dateStr);
      const path = `Companies/${companyName}/MilkSold/${y}/${m}/${d}`;
      const snap = await get(ref(database, path));
      const val = snap.val() || {};
      const list = Object.entries(val).map(([id, record]) => ({
        id,
        ...record,
      }));
      // sort recent first
      list.sort(
        (a, b) =>
          new Date(b.purchaseDate || b.PurchaseDate || dateStr) -
          new Date(a.purchaseDate || a.PurchaseDate || dateStr)
      );
      setSales(list);
    } catch (err) {
      console.error("Failed to load milk sold records", err);
      toast.error("Failed to load milk sold records");
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserRole();
  }, [companyName]);

  useEffect(() => {
    if (companyName) {
      loadSalesForDate(selectedDate);
    }
  }, [selectedDate, companyName]);

  useEffect(() => {
    const totalPages = getMilkSoldTotalPages(sales, recordsPageSize);
    if (recordsPage > totalPages) {
      setRecordsPage(totalPages);
    }
  }, [sales, recordsPage, recordsPageSize]);

  const totals = useMemo(() => {
    return sales.reduce(
      (acc, sale) => {
        const qty = parseFloat(sale.quantity ?? sale.liters ?? 0) || 0;
        const totalAmt = parseFloat(sale.totalAmount ?? sale.TotalAmount ?? 0) || 0;
        acc.liters += qty;
        acc.amount += totalAmt;
        return acc;
      },
      { liters: 0, amount: 0 }
    );
  }, [sales]);

  const { litersByClient, paymentModeDistribution, unitPriceDistribution } = useMemo(() => {
    return buildMilkSoldChartData(sales);
  }, [sales]);

  const recordsTotalPages = useMemo(
    () => getMilkSoldTotalPages(sales, recordsPageSize),
    [sales, recordsPageSize]
  );
  const paginatedSales = useMemo(
    () => paginateMilkSoldItems(sales, recordsPage, recordsPageSize),
    [sales, recordsPage, recordsPageSize]
  );
  const recordsRange = useMemo(
    () => milkSoldRangeForPage(sales, recordsPage, recordsPageSize),
    [sales, recordsPage, recordsPageSize]
  );

  const formatPieChartLabel = ({ name, percent }) => `${name} • ${(percent * 100).toFixed(0)}%`;

  const handleEditSale = (sale) => {
    if (!canManageSales) {
      toast.error("Only CompanyAdmin or Manager can edit sales");
      return;
    }
    setEditingSale(sale);
    setActiveClient({
      id: sale.clientId || sale.clientID || sale.client || sale.clientName || "unknown-client",
      name: sale.clientName || "Unknown Client",
      mode: sale.paymentMode || "Cash",
      balance: sale.balance,
    });
    setShowModal(true);
  };

  const handleDeleteSale = async (sale) => {
    if (!canManageSales) {
      toast.error("Only CompanyAdmin or Manager can delete sales");
      return;
    }
    const confirmMsg = `Delete milk sale for ${sale.clientName || "client"} on ${selectedDate}?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const { y, m, d } = parseDateParts(sale.purchaseDate || selectedDate);
      const purchaseId = sale.purchaseId || sale.id;
      const clientKey =
        sale.clientId || sale.clientID || sale.client || sale.clientName || "unknown-client";
      const pathsToRemove = [
        `Companies/${companyName}/MilkSold/${y}/${m}/${d}/${purchaseId}`,
        `Companies/${companyName}/ClientPurchases/${clientKey}/purchases/${y}/${m}/${purchaseId}`,
        `Companies/${companyName}/NewMilkSaleRecord/${purchaseId}`,
        `NewMilkSaleRecord/${purchaseId}`,
      ];
      await Promise.all(
        pathsToRemove.map(async (p) => {
          try {
            await remove(ref(database, p));
          } catch (err) {
            console.error("Failed to remove path", p, err);
          }
        })
      );
      await logRecordAuditEvent({
        action: "delete",
        entityType: "milk_sale_record",
        companyName,
        recordId: purchaseId,
        sourceComponent: "MilkSoldTab",
        oldData: sale,
      });
      toast.success("Sale deleted");
      loadSalesForDate(selectedDate);
    } catch (err) {
      console.error("Failed to delete sale", err);
      toast.error("Failed to delete sale");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSale(null);
    setActiveClient(null);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Calendar className="text-cyan-700" />
            <div>
              <p className="text-sm text-gray-500">Select date</p>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border rounded px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Total Liters</p>
            <p className="text-xl font-semibold text-cyan-700">
              {totals.liters.toFixed(1)}
            </p>
            <p className="text-sm text-gray-500 mt-1">Total Amount (KES)</p>
            <p className="text-lg font-semibold text-gray-800">
              {totals.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
        {!canManageSales && userRole && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded">
            Only CompanyAdmin or Manager can edit or delete milk sales. Viewing is read-only.
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div
            className="border rounded-lg p-3 xl:col-span-1"
            aria-label={`Client liters distribution on ${selectedDate}`}
          >
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Liters by client</h3>
            <div className="h-64">
              {litersByClient.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={litersByClient}>
                    <XAxis
                      dataKey="client"
                      tick={{ fontSize: 11 }}
                      interval={0}
                      angle={-25}
                      textAnchor="end"
                      height={56}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTooltip
                      formatter={(value) => `${Number(value).toFixed(1)} L`}
                      labelFormatter={(label) => `Client: ${label}`}
                    />
                    <Bar dataKey="liters" fill="#0891b2" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-500">
                  No sales distribution data.
                </div>
              )}
            </div>
          </div>

          <div
            className="border rounded-lg p-3"
            aria-label={`Payment mode distribution on ${selectedDate}`}
          >
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Payment modes</h3>
            <div className="h-64">
              {paymentModeDistribution.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentModeDistribution}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={82}
                      label={formatPieChartLabel}
                    >
                      {paymentModeDistribution.map((entry, index) => (
                        <Cell key={`payment-${entry.name}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value) => [`${value} record${value === 1 ? "" : "s"}`, "Count"]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-500">
                  No payment mode data.
                </div>
              )}
            </div>
          </div>

          <div
            className="border rounded-lg p-3"
            aria-label={`Unit price distribution on ${selectedDate}`}
          >
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Unit price share</h3>
            <div className="h-64">
              {unitPriceDistribution.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={unitPriceDistribution}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={82}
                      label={formatPieChartLabel}
                    >
                      {unitPriceDistribution.map((entry, index) => (
                        <Cell key={`unit-${entry.name}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value) => [`${value} record${value === 1 ? "" : "s"}`, "Count"]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-500">
                  No unit price data.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-600">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-3 text-sm font-semibold text-gray-600">Client</th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-600">Liters</th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-600">Unit Price</th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-600">Total</th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-600">Payment</th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-600">Date</th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sales.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-gray-500" colSpan={7}>
                    No milk sales found for this date.
                  </td>
                </tr>
              ) : (
                paginatedSales.map((sale) => {
                  const liters = sale.quantity ?? sale.liters ?? 0;
                  const unit = sale.unitPrice ?? sale.UnitPrice ?? 0;
                  const total = sale.totalAmount ?? sale.TotalAmount ?? liters * unit;
                  const dateText = new Date(
                    sale.purchaseDate || sale.PurchaseDate || selectedDate
                  ).toLocaleDateString();
                  return (
                    <tr key={sale.id} className="border-t">
                      <td className="px-4 py-3 text-sm text-gray-800">{sale.clientName || "—"}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">{liters}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">
                        {unit ? `KES ${unit}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800">
                        {total ? `KES ${total}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800">
                        {sale.paymentMode || sale.PaymentMode || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800">{dateText}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditSale(sale)}
                            className={`flex items-center gap-1 px-3 py-1 rounded text-white ${
                              canManageSales ? "bg-cyan-600 hover:bg-cyan-700" : "bg-gray-400"
                            }`}
                            disabled={!canManageSales}
                          >
                            <Edit2 size={16} />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteSale(sale)}
                            className={`flex items-center gap-1 px-3 py-1 rounded text-white ${
                              canManageSales ? "bg-red-600 hover:bg-red-700" : "bg-gray-400"
                            }`}
                            disabled={!canManageSales}
                          >
                            <Trash2 size={16} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <div className="flex items-center justify-end gap-3 px-4 py-3 border-t bg-slate-50">
            <div className="text-xs text-gray-500 mr-auto">
              {sales.length > 0
                ? `Showing ${recordsRange.start} - ${recordsRange.end} of ${sales.length}`
                : "No records"}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span>Rows</span>
              <select
                value={recordsPageSize}
                onChange={(e) => {
                  setRecordsPageSize(Number(e.target.value));
                  setRecordsPage(1);
                }}
                className="border rounded px-2 py-1 text-xs"
              >
                {MILK_SOLD_PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={`milk-sold-size-${size}`} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="px-2 py-1 rounded border text-xs disabled:opacity-50"
              onClick={() => setRecordsPage((page) => Math.max(1, page - 1))}
              disabled={recordsPage === 1}
            >
              Prev
            </button>
            <div className="text-xs text-gray-500">
              Page {recordsPage} / {recordsTotalPages}
            </div>
            <button
              className="px-2 py-1 rounded border text-xs disabled:opacity-50"
              onClick={() => setRecordsPage((page) => Math.min(recordsTotalPages, page + 1))}
              disabled={recordsPage >= recordsTotalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <RecordPurchaseModal
          client={activeClient}
          companyName={companyName}
          existingSale={editingSale}
          onClose={closeModal}
          onSaved={() => loadSalesForDate(selectedDate)}
        />
      )}
    </div>
  );
};

export default MilkSoldTab;
